'use client'

import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { type ReactNode, useState } from 'react'

import { inboxV2QueryPersister, installInboxV2PersistSubscription } from '@/lib/queries-v2/persist'

function makeQueryClient() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        // 5 minutes - prevents excessive refetches
        staleTime: 1000 * 60 * 5,
        // 1 hour - garbage collection time
        gcTime: 1000 * 60 * 60,
        // Don't refetch on window focus by default
        refetchOnWindowFocus: false,
        // Retry failed requests once
        retry: 1,
        // Local-first read layer for inbox v2: IndexedDB-backed per-query
        // persister. Installed as a default because TanStack's per-option
        // `persister` typing rejects infinite queries; the persister's own
        // filter predicate (`shouldPersistInboxV2Query`) restricts it to the
        // inbox-v2 query families — every other query passes straight through.
        // Undefined on the server and when IndexedDB is unavailable.
        persister: inboxV2QueryPersister(),
        // TanStack defaults any query WITH a persister to `offlineFirst`,
        // which would change offline behavior app-wide. Pin the standard
        // mode here; the persisted inbox-v2 option factories opt into
        // `offlineFirst` explicitly so their disk restore still works offline.
        networkMode: 'online',
      },
      mutations: {
        // Retry mutations once
        retry: 1,
      },
    },
  })
  // Flush setQueryData writes (realtime patches, optimistic updates, prefetch
  // seeding) on persisted inbox-v2 families to IndexedDB — the persister only
  // writes on queryFn completion. No-op on the server / when persistence is off.
  installInboxV2PersistSubscription(queryClient)
  return queryClient
}

let browserQueryClient: QueryClient | undefined

function getQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return makeQueryClient()
  }
  // Browser: make a new query client if we don't already have one
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient()
  }
  return browserQueryClient
}

interface QueryProviderProps {
  children: ReactNode
}

export function QueryProvider({ children }: QueryProviderProps) {
  // NOTE: Using useState to ensure stable client across renders
  // This avoids hydration issues with React 19
  const [queryClient] = useState(() => getQueryClient())

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}

// Export queryClient for use outside React components (e.g., prefetching)
export { getQueryClient }
